import React, { useState } from "react";
import { useIntl } from "react-intl";
import { getButtonIconComponent, getButtonIconLigature } from "./buttonIconMap";
import {
	getDefaultHoverColor,
	normalizeLetterSpacing,
	normalizeLineHeight,
	getButtonEffectPresentation,
	mergeBoxShadow,
	ensureTextEffectStylesInjected,
	buttonElementNeedsEffectOverflow,
	resolveButtonBorderCss,
} from "./erRendererStyleUtils";
import ButtonImageVisual, { isButtonImageMode } from "./ButtonImageVisual";
import { resolveButtonBorderRadiusCss } from "./buttonBorderRadius";
import { resolveEscapeRoomMediaUrl } from "./erRendererStyleUtils";
import { playEscapeRoomSound } from "../../utils/escapeRoomPlaySound";
import ButtonHideCountdownFrame from "./ButtonHideCountdownFrame";

/** Botón con estado hover (tema igual que backoffice). Subcomponente para poder usar useState. */
export default function ButtonWithHover({ element, style: styleProp, onButtonAction, disabled }) {
	const intl = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	ensureTextEffectStylesInjected();
	const s = element.style || {};
	const content = element.content || {};
	const imageMode = isButtonImageMode(content);
	const btnFx = getButtonEffectPresentation(s, content, { animated: true, disabled });
	const label = content?.label || "";
	const icon = content?.icon || "";
	const bg = s.backgroundColor || "transparent";
	const hasBg = bg && bg !== "transparent";
	const hoverBg = s.hoverBackgroundColor ?? (hasBg ? getDefaultHoverColor(bg) : "rgba(0,0,0,0.06)");
	const showHover = !disabled && isHovered && !!hoverBg;
	const hoverTextColor = (s.hoverColor || "").trim();
	const radiusCss = resolveButtonBorderRadiusCss(s);
	const pulseOn = !!content?.continuousPulse && !disabled;
	const effectOverflow = buttonElementNeedsEffectOverflow(element);
	const hideAfterSec = Math.max(0, Number(content?.hideAfterSeconds) || 0);
	const showHideCountdown = hideAfterSec > 0;
	const triggerAction = () => {
		if (disabled) return;
		const clickSound = content?.clickSoundUrl;
		if (clickSound) {
			playEscapeRoomSound(resolveEscapeRoomMediaUrl(clickSound) || clickSound);
		}
		if (onButtonAction && content?.action) {
			onButtonAction(element);
		}
	};
	const handleClick = (e) => {
		e.stopPropagation();
		triggerAction();
	};
	const handleActivate = (e) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		triggerAction();
	};
	if (imageMode) {
		const { boxShadow: fxBoxShadow, filter: fxFilter, ...fxRest } = btnFx.buttonExtraStyle || {};
		const imageShellStyle = {
			width: "100%",
			height: "100%",
			display: "flex",
			padding: 0,
			backgroundColor: "transparent",
			border: disabled ? resolveButtonBorderCss(s, { disabled: true }) : "none",
			boxShadow: mergeBoxShadow(undefined, fxBoxShadow),
			borderRadius: radiusCss,
			overflow: "hidden",
			cursor: disabled ? "not-allowed" : "pointer",
			...fxRest,
			...(fxFilter ? { filter: fxFilter } : {}),
		};
		const imageInner = (
			<div
				className={["escape-room-button-content", btnFx.buttonClassName].filter(Boolean).join(" ") || undefined}
				style={imageShellStyle}
				onMouseEnter={() => !disabled && setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<ButtonImageVisual content={content} style={s} isHovered={isHovered} disabled={disabled} />
			</div>
		);
		return (
			<div
				className={pulseOn ? "escape-room-button-pulse-wrap" : undefined}
				style={{
					...styleProp,
					overflow: pulseOn || effectOverflow || showHideCountdown ? "visible" : styleProp?.overflow,
					touchAction: "manipulation",
				}}
				onClick={handleClick}
				onPointerUp={handleActivate}
			>
				{showHideCountdown ? (
					<ButtonHideCountdownFrame durationSec={hideAfterSec} borderRadiusCss={radiusCss}>
						{imageInner}
					</ButtonHideCountdownFrame>
				) : (
					imageInner
				)}
			</div>
		);
	}
	const btnFontSize = s.fontSize ?? 16;
	const ta = s.textAlign || "center";
	const flexJustify = ta === "left" ? "flex-start" : ta === "right" ? "flex-end" : "center";
	const btnStyleBase = disabled
		? {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: flexJustify,
			gap: 6,
			backgroundColor: "#e5e7eb",
			color: "#6b7280",
			fontSize: typeof btnFontSize === "number" ? `${btnFontSize}px` : btnFontSize,
			fontWeight: s.fontWeight || "bold",
			fontStyle: s.fontStyle || "normal",
			textDecoration: s.textDecoration || "none",
			...(s.letterSpacing != null && { letterSpacing: normalizeLetterSpacing(s.letterSpacing) }),
			...(s.lineHeight != null && { lineHeight: normalizeLineHeight(s.lineHeight) }),
			fontFamily: s.fontFamily || "Arial, sans-serif",
			borderRadius: radiusCss,
			border: resolveButtonBorderCss(s, { disabled: true }),
			boxShadow: "none",
			cursor: "not-allowed",
		}
		: {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: flexJustify,
			gap: 6,
			backgroundColor: showHover ? hoverBg : bg,
			color: !disabled && isHovered && hoverTextColor ? hoverTextColor : (s.color || "rgba(51, 51, 51, 1)"),
			fontSize: typeof btnFontSize === "number" ? `${btnFontSize}px` : btnFontSize,
			fontWeight: s.fontWeight || "bold",
			fontStyle: s.fontStyle || "normal",
			textDecoration: s.textDecoration || "none",
			...(s.letterSpacing != null && { letterSpacing: normalizeLetterSpacing(s.letterSpacing) }),
			...(s.lineHeight != null && { lineHeight: normalizeLineHeight(s.lineHeight) }),
			fontFamily: s.fontFamily || "Arial, sans-serif",
			borderRadius: radiusCss,
			border: resolveButtonBorderCss(s, { showHover, hasBg }),
			boxShadow: hasBg ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
			cursor: "pointer",
			transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
		};
	const { boxShadow: fxBoxShadow, filter: fxFilter, ...btnFxRest } = btnFx.buttonExtraStyle || {};
	const btnStyle = {
		...btnStyleBase,
		...btnFxRest,
		...(fxFilter ? { filter: fxFilter } : {}),
		boxShadow: mergeBoxShadow(btnStyleBase.boxShadow, fxBoxShadow),
	};
	const labelEffectStyle = btnFx.labelExtraStyle || {};
	const labelEffectClass = btnFx.labelClassName || "";
	const iconEffectStyle = btnFx.iconExtraStyle || {};
	const iconEffectClass = btnFx.iconClassName || "";
	const iconPos = content?.iconPosition || "before";
	const iconScale = content?.iconSize ?? 1.2;
	const iconSizePx = btnFontSize * iconScale;
	const showLabel = String(label || "").trim();
	const showIcon = String(icon || "").trim();
	const displayText = !showLabel && !showIcon ? intl.formatMessage({ id: "QUIZ.CLICK_HERE" }) : null;
	const IconComponent = showIcon ? getButtonIconComponent(icon) : null;
	const iconLigature = showIcon ? getButtonIconLigature(icon) : null;
	const iconSpan = showIcon
		? (iconLigature
			? (
				<span
					className="material-icons"
					aria-hidden
					style={{
						fontSize: iconSizePx,
						lineHeight: 1,
						color: "currentColor",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: iconSizePx,
						height: iconSizePx,
						flexShrink: 0,
					}}
				>
					{iconLigature}
				</span>
			)
			: IconComponent
				? (
					<IconComponent
						size={iconSizePx}
						style={{
							color: "currentColor",
							width: iconSizePx,
							height: iconSizePx,
							flexShrink: 0,
						}}
					/>
				)
				: <span style={{ fontSize: iconSizePx }}>{icon}</span>)
		: null;
	const textSpan = (showLabel || displayText) ? (
		<span className={labelEffectClass || undefined} style={labelEffectStyle}>
			{showLabel || displayText}
		</span>
	) : null;
	const innerBtnStyle = {
		...btnStyle,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
	};
	const textInner = (
		<div
			className={["escape-room-button-content", btnFx.buttonClassName].filter(Boolean).join(" ") || undefined}
			style={innerBtnStyle}
			onMouseEnter={() => !disabled && setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{iconPos === "before" ? iconSpan : textSpan}
			{iconPos === "before" ? textSpan : iconSpan}
		</div>
	);
	return (
		<div
			className={pulseOn ? "escape-room-button-pulse-wrap" : undefined}
			style={{
				...styleProp,
				overflow: pulseOn || effectOverflow || showHideCountdown ? "visible" : styleProp?.overflow,
				touchAction: "manipulation",
				["--er-btn-font"]: s.fontFamily || "Arial, sans-serif",
				["--er-btn-size"]: typeof btnFontSize === "number" ? `${btnFontSize}px` : btnFontSize,
			}}
			onClick={handleClick}
			onPointerUp={handleActivate}
		>
			{showHideCountdown ? (
				<ButtonHideCountdownFrame durationSec={hideAfterSec} borderRadiusCss={radiusCss}>
					{textInner}
				</ButtonHideCountdownFrame>
			) : (
				textInner
			)}
		</div>
	);
}
